import React, { useEffect, useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import NumberInput from '@/components/ui/NumberInput';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { SeccionCobroCompacto } from '../cobros/SeccionCobroCompacto';
import { useCobros, calcularEstadoCobro } from '../../hooks/useCobros';
import { useCobrosPermissions } from '../../hooks/useCobrosPermissions';
import type { OtroTramiteRecord } from '../../types';

interface GestionCobrosDialogProps {
  open: boolean;
  tramite: OtroTramiteRecord | null;
  onOpenChange: (open: boolean) => void;
  onUpdateMontos?: (montoARS: number, montoUSD: number) => Promise<void>;
}

export const GestionCobrosDialog: React.FC<GestionCobrosDialogProps> = ({
  open,
  tramite,
  onOpenChange,
  onUpdateMontos,
}) => {
  const [montoARS, setMontoARS] = useState('0');
  const [montoUSD, setMontoUSD] = useState('0');
  const [isUpdatingMontos, setIsUpdatingMontos] = useState(false);
  const [montosModificados, setMontosModificados] = useState(false);
  const [errorMonto, setErrorMonto] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isEditingMontos, setIsEditingMontos] = useState(false);

  const cobrosData = useCobros(tramite?.id || '');
  const cobrosPermissions = useCobrosPermissions();

  // Calcular estado usando el monto actual del tramite (prop), no el del backend
  const estadoARS = useMemo(() => {
    if (!cobrosData.cobros?.ars || !cobrosData.cobros.ars.iniciado) {
      return {
        totalCobrado: 0,
        porcentajeCobrado: 0,
        saldoPendiente: tramite?.montoARS || 0,
        estaCompleto: false,
      };
    }

    const totalCobrado = cobrosData.cobros.ars.cobros.reduce(
      (sum, cobro) => sum + cobro.monto,
      0
    );
    const montoTotal = tramite?.montoARS || 0;
    const porcentajeCobrado = montoTotal > 0 ? (totalCobrado / montoTotal) * 100 : 0;
    const saldoPendiente = montoTotal - totalCobrado;

    return {
      totalCobrado,
      porcentajeCobrado: Math.min(porcentajeCobrado, 100),
      saldoPendiente: Math.max(saldoPendiente, 0),
      estaCompleto: saldoPendiente <= 0,
    };
  }, [cobrosData.cobros?.ars, tramite?.montoARS]);

  const estadoUSD = useMemo(() => {
    if (!cobrosData.cobros?.usd || !cobrosData.cobros.usd.iniciado) {
      return {
        totalCobrado: 0,
        porcentajeCobrado: 0,
        saldoPendiente: tramite?.montoUSD || 0,
        estaCompleto: false,
      };
    }

    const totalCobrado = cobrosData.cobros.usd.cobros.reduce(
      (sum, cobro) => sum + cobro.monto,
      0
    );
    const montoTotal = tramite?.montoUSD || 0;
    const porcentajeCobrado = montoTotal > 0 ? (totalCobrado / montoTotal) * 100 : 0;
    const saldoPendiente = montoTotal - totalCobrado;

    return {
      totalCobrado,
      porcentajeCobrado: Math.min(porcentajeCobrado, 100),
      saldoPendiente: Math.max(saldoPendiente, 0),
      estaCompleto: saldoPendiente <= 0,
    };
  }, [cobrosData.cobros?.usd, tramite?.montoUSD]);

  // Resetear estado cuando se abre/cierra el diálogo o cuando cambian los montos del tramite
  useEffect(() => {
    if (open && tramite) {
      setMontoARS(String(tramite.montoARS));
      setMontoUSD(String(tramite.montoUSD));
      setMontosModificados(false);
      setErrorMonto(null);
      setSuccessMessage(null);
      setIsEditingMontos(false);
    }
  }, [open, tramite, tramite?.montoARS, tramite?.montoUSD]);

  // Detectar si los montos fueron modificados y validar
  useEffect(() => {
    if (!tramite) return;
    // NumberInput ya devuelve un string con punto decimal, no necesita replace
    const ars = Number.parseFloat(montoARS) || 0;
    const usd = Number.parseFloat(montoUSD) || 0;
    setMontosModificados(ars !== tramite.montoARS || usd !== tramite.montoUSD);

    // Limpiar errores cuando se modifican los montos
    if (errorMonto) {
      setErrorMonto(null);
    }
    // Limpiar mensaje de éxito cuando se modifican los montos
    if (successMessage) {
      setSuccessMessage(null);
    }
  }, [montoARS, montoUSD, tramite]);

  // Validación de montos
  const validationError = useMemo(() => {
    if (!montosModificados) return null;

    // NumberInput ya devuelve un string con punto decimal, no necesita replace
    const montoArsValue = Number.parseFloat(montoARS) || 0;
    const montoUsdValue = Number.parseFloat(montoUSD) || 0;

    // Validar que el monto ARS no sea menor al total cobrado
    if (montoArsValue < estadoARS.totalCobrado) {
      return `El monto ARS no puede ser menor a lo ya cobrado (${new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: 'ARS',
      }).format(estadoARS.totalCobrado)})`;
    }

    // Validar que el monto USD no sea menor al total cobrado
    if (montoUsdValue < estadoUSD.totalCobrado) {
      return `El monto USD no puede ser menor a lo ya cobrado (${new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: 'USD',
      }).format(estadoUSD.totalCobrado)})`;
    }

    // Validar que al menos uno de los montos sea mayor a 0
    if (montoArsValue === 0 && montoUsdValue === 0) {
      return 'Al menos uno de los montos debe ser mayor a cero';
    }

    return null;
  }, [montoARS, montoUSD, estadoARS.totalCobrado, estadoUSD.totalCobrado, montosModificados]);

  const handleGuardarMontos = async () => {
    if (!tramite || !onUpdateMontos) return;

    // Validar antes de enviar
    if (validationError) {
      setErrorMonto(validationError);
      return;
    }

    // NumberInput ya devuelve un string con punto decimal, no necesita replace
    const montoArsValue = Math.max(0, Number.parseFloat(montoARS) || 0);
    const montoUsdValue = Math.max(0, Number.parseFloat(montoUSD) || 0);

    setIsUpdatingMontos(true);
    setErrorMonto(null);
    setSuccessMessage(null);

    try {
      await onUpdateMontos(montoArsValue, montoUsdValue);
      setMontosModificados(false);
      setSuccessMessage('Los montos se actualizaron correctamente');
      setIsEditingMontos(false);

      // Limpiar mensaje de éxito después de 3 segundos
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (error) {
      console.error('Error al actualizar montos:', error);
      const errorMessage = error instanceof Error
        ? error.message
        : 'No pudimos actualizar los montos. Intentá nuevamente.';
      setErrorMonto(errorMessage);
    } finally {
      setIsUpdatingMontos(false);
    }
  };

  if (!tramite) return null;

  const hasMontos = tramite.montoARS > 0 || tramite.montoUSD > 0;
  const formatCurrency = (value: number, currency: 'ARS' | 'USD') =>
    new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(value || 0);

  const handleCancelEdit = () => {
    setMontoARS(String(tramite.montoARS));
    setMontoUSD(String(tramite.montoUSD));
    setMontosModificados(false);
    setErrorMonto(null);
    setSuccessMessage(null);
    setIsEditingMontos(false);
  };

  const handleEditarMontoTotal = async (moneda: 'ARS' | 'USD', nuevoMonto: number) => {
    if (!tramite || !onUpdateMontos) return;

    try {
      if (moneda === 'ARS') {
        await onUpdateMontos(nuevoMonto, tramite.montoUSD);
        setMontoARS(String(nuevoMonto));
      } else {
        await onUpdateMontos(tramite.montoARS, nuevoMonto);
        setMontoUSD(String(nuevoMonto));
      }
      setSuccessMessage('El monto se actualizó correctamente');

      // Limpiar mensaje de éxito después de 3 segundos
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (error) {
      console.error('Error al actualizar monto:', error);
      throw error; // Re-lanzar para que el componente hijo lo maneje
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`max-h-[90dvh] overflow-y-auto ${
          tramite.montoARS > 0 && tramite.montoUSD > 0 ? 'max-w-5xl' : 'max-w-3xl'
        }`}
      >
        <DialogHeader>
          <DialogTitle>Gestión de Cobros</DialogTitle>
          <DialogDescription>
            Administrá los montos y registrá los cobros del trámite de {tramite.otorgantes.map(o => o.nombre).join(', ')}
          </DialogDescription>
          <hr className='mt-4' />
        </DialogHeader>

        <div className="space-y-6">
          {/* Feedback Global - Fuera del recuadro de montos */}
          {/* Mensaje de error del servicio */}
          {errorMonto && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{errorMonto}</AlertDescription>
            </Alert>
          )}

          {/* Mensaje de éxito */}
          {successMessage && (
            <Alert className="border-green-200 bg-green-50 text-green-900">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription>{successMessage}</AlertDescription>
            </Alert>
          )}

          {/* Sección de Cobros */}
          {hasMontos ? (
            <div className="space-y-4">

              <div className={`grid gap-4 ${tramite.montoARS > 0 && tramite.montoUSD > 0 ? 'lg:grid-cols-2' : 'grid-cols-1'}`}>
                {/* Cobros ARS */}
                {tramite.montoARS > 0 && (
                  <SeccionCobroCompacto
                    moneda="ARS"
                    montoTotal={tramite.montoARS}
                    cobros={cobrosData.cobros?.ars?.cobros || []}
                    estado={estadoARS}
                    onRegistrarCobro={(data) => cobrosData.registrarCobro({ moneda: 'ARS', data })}
                    onRegistrarCobroTotal={(cajaId) => cobrosData.registrarCobroTotal({ moneda: 'ARS', cajaId })}
                    onEditarCobro={(cobroId, data) => cobrosData.actualizarCobro({ moneda: 'ARS', cobroId, data })}
                    onEliminarCobro={(cobroId) => cobrosData.eliminarCobro({ moneda: 'ARS', cobroId })}
                    onEditarMontoTotal={(nuevoMonto) => handleEditarMontoTotal('ARS', nuevoMonto)}
                    isLoading={cobrosData.isLoading}
                    canEdit={cobrosPermissions.canCreate}
                  />
                )}

                {/* Cobros USD */}
                {tramite.montoUSD > 0 && (
                  <SeccionCobroCompacto
                    moneda="USD"
                    montoTotal={tramite.montoUSD}
                    cobros={cobrosData.cobros?.usd?.cobros || []}
                    estado={estadoUSD}
                    onRegistrarCobro={(data) => cobrosData.registrarCobro({ moneda: 'USD', data })}
                    onRegistrarCobroTotal={(cajaId) => cobrosData.registrarCobroTotal({ moneda: 'USD', cajaId })}
                    onEditarCobro={(cobroId, data) => cobrosData.actualizarCobro({ moneda: 'USD', cobroId, data })}
                    onEliminarCobro={(cobroId) => cobrosData.eliminarCobro({ moneda: 'USD', cobroId })}
                    onEditarMontoTotal={(nuevoMonto) => handleEditarMontoTotal('USD', nuevoMonto)}
                    isLoading={cobrosData.isLoading}
                    canEdit={cobrosPermissions.canCreate}
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-sm text-gray-500 border rounded-lg bg-gray-50">
              <p>Definí montos para gestionar cobros</p>
              <p className="text-xs mt-1">Ingresá un monto en ARS o USD para comenzar a registrar cobros.</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
